<template>
	<div
		class="login-page"
		v-if="theme"
		:style="{ backgroundColor: theme.appBackgroundColor }"
	>
		<LoaderComponent
			v-if="isLoading"
			:style="{ color: theme.altTextColor }"
		/>
		<main
			id="secure-code"
			:style="{ backgroundColor: theme.secondaryColor }"
		>
			<button
				class="language-change back-button"
				@click="languageChange"
			>
				{{ localizationOposite }} <i class="fas fa-globe"></i>
			</button>
			<div class="form">
				<h1
					v-if="!loginError && sentCode"
					:style="{
						display: 'flex',
						justifyContent: 'center',
						gap: '3px',
						color: theme.loginNoteTextColor,
						fontWeight: '500',
					}"
				>
					<i
						class="fas fa-check-circle fa-lg"
						:style="{ color: theme.successButtonColor }"
					/>
					{{ localization.creditApp.loginPage.codeSentMessage[fimenu.language] }}
				</h1>
				<!--Verify phone text-->
				<h1
					v-if="firstLoad"
					:class="{ 'error-msg': true }"
					:style="{
						display: 'flex',
						justifyContent: 'center',
						gap: '3px',
						fontWeight: '500',
						color: theme.loginNoteTextColor,
					}"
				>
					{{ localization.creditApp.loginPage.verifyPhoneText[fimenu.language] }}
				</h1>
				<span
					v-if="loginError"
					:class="{ 'error-msg': true }"
					:style="{ color: theme.errorButtonColor }"
				>
					<i class="fas fa-exclamation-circle" />
					{{ localization.creditApp.loginPage.quoteLoginError[fimenu.language] }}
				</span>
				<!--A security code will be sent to: ***-***-***-->
				<span
					v-if="firstLoad && !loginError"
					:class="{ 'error-msg': true }"
					:style="{
						backgroundColor: theme.loginNoteBackground,
						color: theme.loginNoteTextColor,
					}"
				>
					{{ localization.creditApp.loginPage.quoteCodeSendCodeNote[fimenu.language] }}
				</span>

				<!--Please enter the 4 digit security code sent to: ***-***-****-->
				<span
					v-if="!firstLoad && !loginError"
					:class="{ 'error-msg': true }"
					:style="{
						backgroundColor: theme.loginNoteBackground,
						color: theme.loginNoteTextColor,
					}"
				>
					{{ localization.creditApp.loginPage.quoteCodeNote[fimenu.language] }}
				</span>
				<span
					:class="{ 'error-msg': true }"
					:style="{
						backgroundColor: theme.loginNoteBackground,
						color: theme.loginNoteTextColor,
					}"
				>
					***-***-{{ phoneLastFour }}
				</span>
				<div
					v-if="!firstLoad"
					id="secure-code-input"
				>
					<InputTextbox
						:style="{ borderBottomColor: theme.mainTextColor }"
						v-model:saturn="phoneCode"
						type="tel"
						:placeholder="localization.creditApp.loginPage.quoteLoginFormPlaceholder[fimenu.language]"
						@keyEnter="requestApplicationVerification"
					/>
					<button
						id="resend-code-btn"
						class="link send-code"
						:style="{ color: theme.linkSecondaryTextColor }"
						@click="sendNewVerificationCode"
					>
						{{ localization.creditApp.loginPage.resendVerificationCode[fimenu.language] }}
					</button>
				</div>
			</div>
			<!--Send Code button-->
			<button
				v-if="firstLoad"
				class="button-full"
				:style="{
					backgroundColor: theme.successButtonColor,
					color: theme.successButtonTextColor,
				}"
				:disabled="isLoading"
				@click="sendInitialCode"
			>
				{{ localization.creditApp.loginPage.quoteLoginSendCodeButton[fimenu.language] }}
			</button>
			<!--Continue button-->
			<button
				v-if="!firstLoad"
				class="button-full"
				:style="{
					backgroundColor: theme.successButtonColor,
					color: theme.successButtonTextColor,
				}"
				:disabled="disabledContinue"
				@click="requestApplicationVerification"
			>
				{{ localization.creditApp.loginPage.quoteLoginButton[fimenu.language] }}
			</button>
		</main>
	</div>
	<div class="center-div">
		<Widget
			v-if="errorCode"
			title="Sorry!"
			:subtext="`An error has occurred: ${errorCode}. We apologize for the inconvenience. Please have your salesperson send you a new link.`"
		/>
	</div>
</template>

<script>
	import api from '@core/services/api/_api';
	import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
	import ENUMS from '@core/classes/Enums';
	import InputTextbox from '@/components/customerApp/InputTextbox.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import LOCALIZATION from '@core/classes/Localization';
	import moment from 'moment';
	import { required } from '@vuelidate/validators';
	import settings from 'settings';
	import { useRedirectStore } from '@core/stores/RedirectStore';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';
	import Widget from '@/components/customerPortal/reusableComponents/Widget.vue';

	//NOTE: This is the Credit APP OTP Page, that happens after the "Start Application" button gets clicked on LoginSuccess.vue or when they land on here after getting the Send Portal Link text or email. Renamed this to just OTP Page for Customer Portal. Previously called "CreditAppLogin.vue"
	export default {
		name: 'OTPPage',
		props: ['fimenu'],
		setup() {
			const redirectStore = useRedirectStore();
			return { v$: useVuelidate({ $scope: false }), redirectStore };
		},
		data() {
			return {
				phoneCode: null,
				theme: null,
				loginError: false,
				isLoading: false,
				firstLoad: true,
				pageInitialHeight: 0,
				pageInitialWidth: 0,
				editId: null,
				codeId: null,
				errorCode: null,
				phoneLastFour: 'the number on file', // string
				sentCode: false, // boolean
			};
		},
		computed: {
			localization() {
				return LOCALIZATION;
			},
			disabledContinue() {
				return this.v$.$invalid;
			},
			localizationOposite() {
				if (this.fimenu.language == 'Spanish') {
					return 'English';
				} else {
					return 'Español';
				}
			},
			util() {
				return util;
			},
			ENUMS() {
				return ENUMS;
			},
			/**
			 * Check to see if we have a redirect ID. If not, then we force send.
			 **/
			needNewVerificationCode() {
				const latestEditId = localStorage.getItem('editId');
				const currentEditId = this.$route.query.editId;

				if (currentEditId != null && (latestEditId == null || latestEditId != currentEditId)) {
					// console.log("NEED FOR NEW CODE");
					localStorage.setItem('editId', currentEditId);
					return true;
				}
				// console.log("NO NEED FOR NEW CODE");
				return false;
			},
		},
		async created() {
			this.pageInitialHeight = window.innerHeight;
			window.addEventListener('resize', this.onResizeHandler);

			this.fimenu.language = this.$route.query.language ? this.$route.query.language : 'English';
			this.getStoreTheme();

			if (this.$route.query.errorCode) this.errorCode = this.$route.query.errorCode;
			if (this.$route.query.lastFour) this.phoneLastFour = this.$route.query.lastFour;
		},
		validations() {
			const validations = {
				phoneCode: {
					required,
				},
			};
			return validations;
		},
		methods: {
			async getStoreTheme() {
				try {
					if (!this.$route.query.storeCode)
						return this.$router.push({
							name: 'OTPPage',
							query: { errorCode: '98765' },
						});
					let response = await api.settings.getStoreBasicInfo(this.$route.query.storeCode);
					if (response.data) {
						if (response.data.theme.mainTextColor) {
							if (response.data.storeFiles !== null && response.data.storeFiles.length > 0) {
								let { filepath, filetype } = response.data.storeFiles.find(f => f.filetype === ENUMS.STORE_FILE_TYPES.MAIN_LOGO);
								response.data.theme.logoSrc = filepath
									? `${settings.apiUrl}/Settings/GetStoreMainLogo?storeCode=${this.$route.query.storeCode}&ver=${Date.now()}`
									: null;
							}
						} else {
							response.data.theme = ApplicationsSettingsLoader.ApplicationSettings.theme;
						}

						this.fimenu.storeInfo = response.data;
						this.theme = this.fimenu.storeInfo.theme;
						localStorage.setItem('fimenuObject', JSON.stringify(this.fimenu));
					}
				} catch (error) {
					console.error('err', error);
				}
			},
			populateFimenu(obj) {
				Object.keys(this.fimenu).forEach(fi => {
					this.fimenu[fi] = obj[fi];
				});
			},
			onResizeHandler() {
				const metaViewport = document.querySelector('meta[name=viewport]');

				if (window.innerHeight < this.pageInitialHeight) {
					// document.documentElement.style.setProperty('overflow', 'auto')
					metaViewport.setAttribute('content', 'height=' + this.pageInitialHeight + 'px,width=device-width,initial-scale=1.0');
				} else {
					metaViewport.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=0');
				}
			},
			/**
			 * This method sends an email code to the customer and determines if they need a new code for OTP, and generates one.
			 */
			async sendInitialCode() {
				this.firstLoad = false;
				this.isLoading = true;
				try {
					const response = await api.applications.verifyEmailAndSendPhoneCode(this.$route.query.emailCode, this.needNewVerificationCode, false);
					this.sentCode = true;
					this.codeId = response.data;

					setTimeout(() => {
						this.sentCode = false;
					}, 10000); // 10 seconds
				} catch (error) {
					console.error(error);
				} finally {
					this.isLoading = false;
				}
			},
			/**
			 * This method verifies the OTP that was generated & valid
			 */
			async requestApplicationVerification() {
				this.isLoading = true;
				const isEmailVerified = !this.$route.query.isInStore;
				const isEditMode = this.$route.query.questionKey ? true : false;
				try {
					const response = await api.applications.verifyPhoneEmailCode(this.$route.query.emailCode, this.phoneCode, this.codeId, isEmailVerified, isEditMode);
					if (response.data) {
						localStorage.removeItem('applicationObject');
						localStorage.setItem('QuoteApplication', util.encryptJSONText(this.phoneCode));
						localStorage.removeItem('customerId');
						localStorage.setItem('customerId', util.encryptJSONText(JSON.stringify(response.data.customerId)));

						const paramsResponse = await api.applications.getCreateApplicationQueryParams(response.data.applicationId);
						const queryParams = new URLSearchParams(paramsResponse.data ?? {});

						// Old applications will not have a leadSource so we set it for backwards compatibility
						if (!queryParams.get('leadSource')) {
							queryParams.set('leadSource', 'instore');
						}

						let editModeLink = `/creditapp/creditappQuestionnaire?id=${response.data.applicationId}&questionKey=${this.$route.query.questionKey}&${queryParams}`;
						if (this.$route.query.isSingleQuestionEdit) queryParams.set('isSingleQuestionEdit', 'true');
						if (this.$route.query.resetDateCompleted) {
							editModeLink += '&resetDateCompleted=true';
						}

						const emailVerifiedOutOfStoreLink = `/creditapp/creditappQuestionnaire?emailCode=${this.$route.query.emailCode}&${queryParams}`;

						const emailNotVerifiedLink = `/creditapp/creditappQuestionnaire?emailCode=${this.$route.query.emailCode}&${queryParams}`;

						this.navigateWithParams(
							response.data.customerId,
							this.fimenu,
							this.$route.query.emailCode,
							isEmailVerified,
							isEditMode,
							editModeLink,
							emailVerifiedOutOfStoreLink,
							emailNotVerifiedLink,
						);
					} else {
						this.loginError = true;
					}
				} catch (error) {
					console.error(error);
					this.loginError = true;
				} finally {
					this.isLoading = false;
				}
			},

			navigateWithParams(customerId, fimenu, emailCode, isEmailVerified, isEditMode, editModeLink, emailVerifiedOutOfStoreLink, emailNotVerifiedLink) {
				this.redirectStore.setRedirectParams({
					customerId: customerId,
					fimenu: fimenu,
					emailCode: emailCode,
					isEmailVerified: isEmailVerified,
					isEditMode: isEditMode,
					editModeLink: editModeLink,
					emailVerifiedOutOfStoreLink: emailVerifiedOutOfStoreLink,
					emailNotVerifiedLink: emailNotVerifiedLink,
				});
				this.$router.push('/customer-portal/redirect-page');
			},
			sendNewVerificationCode() {
				this.isLoading = true;
				this.phoneCode = null;
				api.applications
					.verifyEmailAndSendPhoneCode(this.$route.query.emailCode, true, true)
					.then(response => {
						if (response.data) {
							this.sentCode = true;
							this.firstLoad = false;
							this.loginError = false;
							this.codeId = response.data;

							setTimeout(() => {
								this.sentCode = false;
							}, 10000); // 10 seconds
						} else {
							this.loginError = true;
							this.firstLoad = false;
						}
					})
					.catch(error => {
						console.error(error);
						this.loginError = true;
						this.firstLoad = false;
					})
					.finally(() => {
						this.isLoading = false;
					});
			},
			languageChange() {
				if (this.fimenu.language == 'English') {
					this.fimenu.language = 'Spanish';
				} else {
					this.fimenu.language = 'English';
				}
			},
		},
		components: {
			InputTextbox,
			LoaderComponent,
			Widget,
		},
	};
</script>
<style>
	.login-page header > * {
		opacity: 0;
	}

	.center-div {
		display: flex;
		justify-content: center;
	}
</style>
